/**
 * Chart element payload → typed `FmChartSpec`.
 *
 * Story descriptors carry served rows plus semantic series; this adapter names
 * which chart kind each story chart is and how its series are coloured. It
 * never fills a missing value: non-finite cells stay gaps.
 */
import type {
  FmBarSeries,
  FmChartRow,
  FmChartSpec,
  FmSeriesColor,
  FmTimeSeriesLine,
} from '../charts/chartTypes';
import { ChartPaletteError, isAssortmentKey, parseSpeciesCode } from '../charts/palette';
import type {
  FmStoryAccentTone,
  FmStoryChart,
  FmStoryChartDataValue,
  FmStoryChartReference,
  FmStoryChartSeries,
  FmStoryDiameterDistributionEntry,
} from './elementFields.types';
import type { FmStoryChartMediaSlot } from './elementMedia.types';

const DEFAULT_CHART_VALUE_KEY = 'value';
const DEFAULT_CHART_TONE_KEY = 'tone';

function finiteValue(value: FmStoryChartDataValue): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function rows(chart: { data: readonly Record<string, FmStoryChartDataValue>[] }): FmChartRow[] {
  return chart.data.map((row) => ({ ...row }));
}

function toneColor(tone: FmStoryAccentTone | undefined, index: number): FmSeriesColor {
  return tone === undefined ? { by: 'sequence', index } : { by: 'tone', tone };
}

function seriesKey(series: FmStoryChartSeries): string {
  return series.dataKey ?? String(series.code);
}

function speciesShareSeries(
  series: readonly FmStoryChartSeries[],
  mode: 'species' | 'assortment',
): FmBarSeries[] {
  return series.map((entry) => {
    const code = String(entry.code);
    if (mode === 'assortment' && !isAssortmentKey(code)) {
      throw new ChartPaletteError(
        `Unknown assortment key "${code}": no palette colour is defined for it.`,
      );
    }
    const color: FmSeriesColor = isAssortmentKey(code)
      ? { by: 'assortment', key: code }
      : { by: 'species', code: parseSpeciesCode(entry.code) };
    return {
      dataKey: seriesKey(entry),
      label: entry.label,
      color,
      stackKey: entry.stackKey ?? 'stack',
      role: entry.role,
    };
  });
}

/** A served chart names its value; printing the raw data key would leak it into the UI. */
function requiredValueLabel(chart: {
  kind: string;
  valueKey: string;
  valueLabel?: string;
}): string {
  if (!chart.valueLabel?.trim()) {
    throw new Error(
      `A ${chart.kind} story chart needs a valueLabel for its value "${chart.valueKey}".`,
    );
  }
  return chart.valueLabel;
}

function requiredReferenceLabel(kind: string, reference: FmStoryChartReference): string {
  if (!reference.valueLabel.trim()) {
    throw new Error(
      `A ${kind} story chart needs a valueLabel for its reference "${reference.valueKey}".`,
    );
  }
  return reference.valueLabel;
}

function growthSeries(chart: Extract<FmStoryChart, { kind: 'growth-trajectory' }>) {
  if (chart.series) {
    return chart.series.map((entry, index) => ({
      key: String(entry.code),
      label: entry.label,
      color: toneColor(entry.tone, index),
    }));
  }
  return [
    {
      key: chart.valueKey,
      label: requiredValueLabel(chart),
      color: { by: 'tone', tone: 'accent' } as const,
    },
  ];
}

function timeSeriesLine(entry: FmStoryChartSeries, index: number): FmTimeSeriesLine {
  return {
    dataKey: seriesKey(entry),
    label: entry.label,
    color: toneColor(entry.tone, index),
    variant: entry.role === 'reference' ? 'secondary' : 'dominant',
  };
}

function diameterSeries(entries: readonly FmStoryDiameterDistributionEntry[]) {
  return entries.map((entry) => ({
    speciesCode: entry.speciesCode,
    label: entry.label,
    bins: entry.bins,
  }));
}

function histogramSpec(
  chart: Extract<FmStoryChart, { kind: 'diameter-distribution' }>,
  unit: string,
): FmChartSpec {
  const base = {
    kind: 'histogram' as const,
    distributions: diameterSeries(chart.distributions),
    xLabel: chart.xAxisLabel,
    yLabel: unit,
    presentation: 'story' as const,
  };
  if (chart.removedDistributions === undefined) {
    if (chart.keptLabel !== undefined) {
      throw new Error('A diameter distribution draws kept stems only beside a served cut.');
    }
    return base;
  }
  if (chart.cutLabel === undefined) {
    throw new Error('A diameter distribution with removed stems needs its cutLabel.');
  }
  return {
    ...base,
    removedDistributions: diameterSeries(chart.removedDistributions),
    cutLabel: chart.cutLabel,
    keptLabel: chart.keptLabel,
  };
}

function donutSpec(chart: Extract<FmStoryChart, { kind: 'share-donut' }>): FmChartSpec {
  const valueKey = chart.valueKey ?? DEFAULT_CHART_VALUE_KEY;
  const toneKey = chart.toneKey ?? DEFAULT_CHART_TONE_KEY;
  return {
    kind: 'donut',
    activeId: chart.activeKey,
    slices: chart.data.flatMap((row, index) => {
      const value = finiteValue(row[valueKey]);
      if (value === null || value <= 0) return [];
      const label = String(row[chart.xKey]);
      const speciesCode = chart.speciesCodeKey ? row[chart.speciesCodeKey] : undefined;
      const color: FmSeriesColor =
        chart.colorMode === 'species'
          ? { by: 'species', code: parseSpeciesCode(String(speciesCode ?? label)) }
          : toneColor(row[toneKey] as FmStoryAccentTone | undefined, index);
      return [{ id: String(row.id ?? label), label, value, color }];
    }),
  };
}

/** Whether a served chart has anything to plot; an empty series is never drawn as zero. */
export function storyChartHasData(chart: FmStoryChart): boolean {
  switch (chart.kind) {
    case 'diameter-distribution':
      return chart.distributions.length > 0;
    case 'cashflow':
      return chart.periods.length > 0;
    case 'corridor-rows':
      return chart.rows.length > 0;
    default:
      return chart.data.length > 0;
  }
}

/** Only the served chart, its unit and the number locale shape the spec; the chrome stays with the caller. */
export function chartElementSpec(
  slot: Pick<FmStoryChartMediaSlot, 'chart' | 'unit' | 'locale'>,
): FmChartSpec {
  const { chart, unit } = slot;
  const format = new Intl.NumberFormat(slot.locale, { maximumFractionDigits: 0 });
  const formatValue = (value: number): string => format.format(value);
  switch (chart.kind) {
    case 'share-donut':
      return donutSpec(chart);
    case 'growth-trajectory':
      return {
        kind: 'growth',
        data: rows(chart),
        xKey: chart.xKey,
        xLabel: chart.xAxisLabel,
        yLabel: unit,
        series: growthSeries(chart),
        render: chart.render ?? 'line',
        formatValue,
      };
    case 'time-series': {
      const tick = new Intl.NumberFormat(slot.locale, { maximumFractionDigits: 1 });
      const precise = new Intl.NumberFormat(slot.locale, { maximumFractionDigits: 2 });
      return {
        kind: 'time-series',
        data: rows(chart),
        xKey: chart.xKey,
        yLabel: unit,
        legend: chart.legend,
        series: [
          timeSeriesLine(chart.series[0], 0),
          ...chart.series.slice(1).map((entry, index) => timeSeriesLine(entry, index + 1)),
        ],
        onRangeChange: chart.onRangeChange,
        formatXAxisTick: (value) => /^\d{4}/.exec(value)?.[0] ?? value,
        formatTick: (value) => tick.format(value),
        formatValue: (value) => `${precise.format(value)} ${unit}`,
      };
    }
    case 'diameter-distribution':
      return histogramSpec(chart, unit);
    case 'cashflow': {
      const currency = new Intl.NumberFormat(slot.locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      });
      return {
        kind: 'cashflow',
        render: chart.presentation,
        data: chart.periods,
        xLabel: chart.xAxisLabel,
        yLabel: chart.valueLabel,
        seriesLabels: chart.seriesLabels,
        formatValue: (value) => currency.format(value),
      };
    }
    case 'corridor-rows': {
      const basalArea = new Intl.NumberFormat(slot.locale, { maximumFractionDigits: 1 });
      return {
        kind: 'corridor-rows',
        rows: chart.rows,
        xLabel: unit,
        bandLabel: chart.bandLabel,
        floorLabel: chart.floorLabel,
        residualLabel: chart.residualLabel,
        formatValue: (value) => basalArea.format(value),
      };
    }
    case 'species-share':
      return {
        kind: 'bar',
        data: rows(chart),
        xKey: chart.xKey,
        xLabel: chart.xAxisLabel,
        yLabel: unit,
        series: speciesShareSeries(chart.series, chart.mode),
        activeSeriesKey: chart.activeKey,
        formatValue,
      };
    case 'ranked-bar':
    case 'value-growth': {
      const color: FmSeriesColor = {
        by: 'economics',
        key: chart.kind === 'value-growth' ? 'net' : 'income',
      };
      const value: FmBarSeries = {
        dataKey: chart.valueKey,
        label: requiredValueLabel(chart),
        color,
      };
      // Distinct stack keys set the reference bar beside the value bar rather
      // than on top of it; the role alone makes it the quieter one.
      const series: FmBarSeries[] = chart.reference
        ? [
            { ...value, stackKey: 'value', role: 'result' },
            {
              dataKey: chart.reference.valueKey,
              label: requiredReferenceLabel(chart.kind, chart.reference),
              color,
              stackKey: 'reference',
              role: 'reference',
            },
          ]
        : [value];
      return {
        kind: 'bar',
        data: rows(chart),
        xKey: chart.xKey,
        xLabel: chart.xAxisLabel,
        yLabel: unit,
        series,
        activeCategory: chart.activeKey,
        formatValue,
      };
    }
  }
}
